import { useState } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Card, Btn } from "../ui/index.jsx";

/**
 * PricingPage v3 — Sprint 26-1 / s26p11
 *
 * s26p11 변경: 무료 다운그레이드(Core/Starter → Free) 처리 추가
 * - 유료 사용자가 "무료로 변경" 누르면 확인창 표시
 * - 확인 시 /subscription/cancel 호출 (현재 기간 만료 후 Free 전환)
 * - 신규/무료 사용자는 기존처럼 onSelectPlan("free") 위임
 */

const PRICING_HEADER_COPY = "한 번의 뇌동매매만 막아도, 구독료를 뽑습니다";
const PRICING_SUB_COPY = "Pause는 매수·매도를 권하지 않습니다. 흔들릴 때 데이터 정리를 받아보세요.";

export default function PricingPage({ onBack, currentPlan, onSelectPlan }) {
  const [cycle, setCycle] = useState("monthly"); // monthly | annual

  // 무료 다운그레이드 확인창 상태
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [downgradeDone, setDowngradeDone] = useState(false);
  const [downgradeError, setDowngradeError] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);

  const isPaid = currentPlan && currentPlan !== "free";

  // VAT 계산 헬퍼 (10%)
  const withVat = (n) => Math.round(n * 1.1);

  // 무료로 변경(다운그레이드) 처리
  const handleDowngrade = async () => {
    setDowngradeLoading(true);
    setDowngradeError(null);
    try {
      const token = localStorage.getItem("phase_token");
      const res = await api(
        "/subscription/cancel",
        { method: "POST", body: JSON.stringify({ reason: "무료 플랜으로 변경" }) },
        token
      );
      setPeriodEnd(res?.periodEnd || null);
      setDowngradeDone(true);
    } catch (e) {
      setDowngradeError(e?.message || "변경 처리 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.");
    }
    setDowngradeLoading(false);
  };

  // 플랜 버튼 클릭 핸들러
  const handleSelect = (planId) => {
    if (planId === currentPlan) return;
    if (planId === "free") {
      // 유료 사용자 → 확인창, 무료/신규 사용자 → 기존 위임
      if (isPaid) {
        setDowngradeOpen(true);
      } else {
        onSelectPlan?.("free", cycle);
      }
      return;
    }
    onSelectPlan?.(planId, cycle);
  };

  // 플랜 정의 (백엔드 subscriptionGuard PLAN_LIMITS와 일치)
  const plans = [
    {
      id: "free",
      name: "Free",
      monthly: 0,
      annual: 0,
      color: T.textDim,
      desc: "Pause 맛보기",
      persona: "F · 호기심형",
      features: [
        { text: "종목 1개 등록" },
        { text: "PM 리포트 (1일 1회)" },
        { text: "AI 상담 월 3회" },
        { text: "시장 Q&A 불가", dim: true },
        { text: "대화 기록 최근 3일" },
        { text: "기본 상태 확인" },
      ],
    },
    {
      id: "starter",
      name: "Starter",
      monthly: 9900,
      annual: 99000,
      annualMonthlyEq: 8250,
      color: T.blue,
      desc: "본격적인 관리 시작",
      persona: "E · 본전 심리형",
      features: [
        { text: "종목 3개 등록", highlight: true },
        { text: "AM/PM 리포트 (1일 2회)", highlight: true },
        { text: "AI 상담 월 30회", highlight: true },
        { text: "시장 Q&A 포함", highlight: true },
        { text: "대화 기록 30일 보관" },
        { text: "행동 기록 + 수익률 추적", highlight: true },
        { text: "PER/PBR 밸류에이션" },
        { text: "포트폴리오 비중 분석" },
      ],
    },
    {
      id: "core",
      name: "Core",
      monthly: 19900,
      annual: 199000,
      annualMonthlyEq: 16583,
      color: T.accent,
      desc: "흔들릴 때 가장 강한 무기",
      persona: "D · 적극 매매형",
      features: [
        { text: "종목 10개 등록", highlight: true },
        { text: "AM/PM 리포트 (1일 2회)" },
        { text: "AI 상담 월 250회", highlight: true },
        { text: "시장 Q&A 월 250회 (합산)", highlight: true },
        { text: "대화 기록 무제한 보관", highlight: true },
        { text: "투자 메모리", highlight: true },
        { text: "일일 브리핑 (오늘의 핵심)", highlight: true },
        { text: "업종 대비 밸류에이션", highlight: true },
        { text: "섹터 등락률 분석", highlight: true },
        { text: "투자 여정 타임라인", highlight: true },
        { text: "원칙 이탈 알림", highlight: true },
        { text: "행동 패턴 분석", highlight: true },
        { text: "우선 이메일 지원" },
      ],
      popular: true,
    },
  ];

  const cycleBtnStyle = (active) => ({
    flex: 1,
    padding: "10px 16px",
    borderRadius: 8,
    border: active ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
    background: active ? T.accent + "15" : "transparent",
    color: active ? T.accent : T.textDim,
    fontSize: 13,
    fontWeight: active ? 800 : 600,
    cursor: "pointer",
    position: "relative",
    transition: "all 0.15s ease",
  });

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Btn variant="ghost" style={{ padding: "6px 12px" }} onClick={onBack}>←</Btn>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>요금제</div>
          <div style={{ fontSize: 12, color: T.textDim }}>나에게 맞는 플랜을 선택하세요</div>
        </div>
      </div>

      {/* ROI 헤더 카피 */}
      <div style={{
        background: `linear-gradient(135deg, ${T.accent}15 0%, ${T.accent}05 100%)`,
        border: `1px solid ${T.accent}30`,
        borderRadius: 14,
        padding: "20px 18px",
        marginBottom: 24,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: "clamp(15px, 3vw, 18px)",
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.45,
          marginBottom: 6,
        }}>
          {PRICING_HEADER_COPY}
        </div>
        <div style={{
          fontSize: "clamp(11px, 2.2vw, 13px)",
          color: T.textDim,
          lineHeight: 1.5,
        }}>
          {PRICING_SUB_COPY}
        </div>
      </div>

      {/* 월간/연간 토글 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, background: T.card, padding: 4, borderRadius: 10, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
        <button type="button" style={cycleBtnStyle(cycle === "monthly")} onClick={() => setCycle("monthly")}>
          월간 결제
        </button>
        <button type="button" style={cycleBtnStyle(cycle === "annual")} onClick={() => setCycle("annual")}>
          연간 결제
          <span style={{ position: "absolute", top: -8, right: -4, background: T.accent, color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 6 }}>
            2개월 무료
          </span>
        </button>
      </div>

      {/* 무료 다운그레이드 확인창 (유료 사용자가 무료 누를 때) */}
      {downgradeOpen && (
        <div style={{
          background: T.card, border: `1px solid ${T.accent}40`, borderRadius: 14,
          padding: "20px 18px", marginBottom: 24,
        }}>
          {!downgradeDone ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 8 }}>
                무료 플랜으로 변경하시겠어요?
              </div>
              <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.7, marginBottom: 16 }}>
                무료로 변경하셔도 현재 구독 기간이 끝날 때까지는 지금 플랜을 그대로 이용할 수 있어요.
                다음 결제는 진행되지 않으며, 기간이 끝나면 자동으로 Free 플랜으로 전환됩니다.
              </div>

              {downgradeError && (
                <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{downgradeError}</div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <Btn
                  variant="ghost"
                  style={{ flex: 1, justifyContent: "center" }}
                  onClick={() => { setDowngradeOpen(false); setDowngradeError(null); }}
                  disabled={downgradeLoading}
                >
                  취소
                </Btn>
                <Btn
                  variant="primary"
                  style={{ flex: 1, justifyContent: "center" }}
                  onClick={handleDowngrade}
                  disabled={downgradeLoading}
                >
                  {downgradeLoading ? "처리 중..." : "무료로 변경"}
                </Btn>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 8 }}>
                무료 플랜 변경이 예약됐어요
              </div>
              <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.7 }}>
                {periodEnd
                  ? `${periodEnd.slice(0, 10)}까지는 현재 플랜을 그대로 이용할 수 있어요. 이후 자동으로 Free 플랜으로 전환됩니다.`
                  : "현재 구독 기간이 끝나면 자동으로 Free 플랜으로 전환됩니다."}
              </div>
            </>
          )}
        </div>
      )}

      {/* 플랜 카드 그리드 */}
      <div className="phase-pricing-grid" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 14,
      }}>
        {plans.map((plan) => {
          const isFree = plan.id === "free";
          const price = cycle === "annual" ? plan.annual : plan.monthly;
          const priceVat = isFree ? 0 : withVat(price);
          const periodLabel = cycle === "annual" ? "/년" : "/월";
          const priceDisplay = isFree ? "0원" : `${price.toLocaleString()}원`;

          const mobileOrder = plan.popular ? -1 : isFree ? 1 : 0;

          // 버튼 텍스트 결정
          let btnText;
          if (plan.id === currentPlan) {
            btnText = "현재 플랜";
          } else if (plan.id === "free") {
            btnText = isPaid ? "무료로 변경" : "무료 시작";
          } else {
            btnText = "선택하기";
          }

          return (
            <div
              key={plan.id}
              style={{
                background: T.card,
                borderRadius: 16,
                padding: "24px 20px",
                border: `1px solid ${plan.popular ? plan.color : T.border}`,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                boxShadow: plan.popular ? `0 0 20px ${plan.color}25` : "none",
                order: mobileOrder,
              }}
            >
              {plan.popular && (
                <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", padding: "3px 14px", background: plan.color, borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#fff" }}>
                  추천
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: plan.color, fontWeight: 700, letterSpacing: 0.5 }}>{plan.name}</span>
                <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, padding: "2px 6px", background: T.bg, borderRadius: 4 }}>
                  {plan.persona}
                </span>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>{plan.desc}</div>

              {/* 가격 표시 (VAT 별도 + 연결제 분리) */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontSize: 28, fontWeight: 900, color: T.text }}>{priceDisplay}</span>
                  {!isFree && <span style={{ fontSize: 12, color: T.textMuted }}>{periodLabel}</span>}
                </div>

                {!isFree && (
                  <>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                      VAT 별도 · 실결제 {priceVat.toLocaleString()}원
                    </div>

                    {cycle === "annual" && (
                      <div style={{ fontSize: 11, color: T.accent, fontWeight: 600, marginTop: 4 }}>
                        월 {plan.annualMonthlyEq.toLocaleString()}원 꼴
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* 기능 목록 */}
              <div style={{ flex: 1, marginBottom: 16 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ fontSize: 12, color: f.dim ? T.textMuted + "80" : f.highlight ? T.text : T.textDim, padding: "5px 0", display: "flex", gap: 6, fontWeight: f.highlight ? 600 : 400 }}>
                    <span style={{ color: f.dim ? T.textMuted + "50" : plan.color, flexShrink: 0 }}>{f.dim ? "×" : "✓"}</span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>

              {/* 선택 버튼 */}
              <Btn
                variant={plan.id === currentPlan ? "ghost" : plan.popular ? "primary" : "ghost"}
                style={{ width: "100%", justifyContent: "center", opacity: plan.id === currentPlan ? 0.5 : 1 }}
                onClick={() => handleSelect(plan.id)}
                disabled={plan.id === currentPlan}
              >
                {btnText}
              </Btn>
            </div>
          );
        })}
      </div>

      {/* 하단 안내 */}
      <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: T.textMuted, lineHeight: 1.8 }}>
        {cycle === "annual"
          ? "연간 결제는 1년치 일시 결제 · 만료 7일 전 이메일 안내 · VAT 별도"
          : "월간 결제는 매월 자동 갱신 · 언제든 해지 가능 · VAT 별도"}
        <div style={{ marginTop: 6, fontSize: 10, color: T.textMuted + "AA" }}>
          현재 가격은 초기 운영 기준입니다. 향후 기능 추가에 따라 변경될 수 있으며, 변경 시 최소 30일 전 안내드립니다.
        </div>
      </div>
    </div>
  );
}
